import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faQuestionCircle} from "@fortawesome/free-solid-svg-icons";

// Style
import {voice} from "../style/components/typography";
import {flex, z} from "../style/components/mixins";
import {pad, radius, shadow, transition} from "../style/components/variables";
import {Text} from "../style/components/general";
import {isMobile} from "../utils/responsive";

const Help = ({
  children,
  title = "",
  icon,
  color,
  inverted = false,
  showModal = isMobile(),
  left = false,
  visible: controlledVisible,
  setVisible: controlledSetVisible
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const isControlled = controlledSetVisible !== undefined;
  const visible = isControlled ? controlledVisible : internalVisible;
  const setVisible = isControlled ? controlledSetVisible : setInternalVisible;

  const outside = useRef(null);
  const tooltip = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!(visible && !showModal && outside.current)) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!outside.current) return;

      const {top, left: anchorLeft, right: anchorRight, bottom} =
        outside.current.getBoundingClientRect();
      const gap = pad / 2;
      const width = tooltip.current?.offsetWidth || 0;
      const height = tooltip.current?.offsetHeight || 0;
      const measured = Boolean(tooltip.current);
      const viewportWidth = window.innerWidth;

      const below = Boolean(height) && top - height + gap < gap;
      const nextTop = below ? bottom : top;

      let nextLeft;
      let nextRight;
      if (left) {
        let rightOffset = viewportWidth - anchorLeft + gap;
        if (width) rightOffset = Math.min(rightOffset, Math.max(gap, viewportWidth - width - gap));
        nextRight = rightOffset;
      } else {
        let leftOffset = anchorRight + gap;
        if (width && leftOffset + width > viewportWidth - gap)
          leftOffset = Math.max(gap, viewportWidth - width - gap);
        nextLeft = leftOffset;
      }

      const next = {top: nextTop, left: nextLeft, right: nextRight, below, measured};
      setPosition(prev => {
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.right === next.right &&
          prev.below === next.below &&
          prev.measured === next.measured
        )
          return prev;
        return next;
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [left, showModal, visible, position?.measured]);

  useEffect(() => {
    const handleClickOutside = ({target}) =>
      outside &&
      outside.current !== null &&
      !outside.current.contains(target) &&
      !tooltip.current?.contains(target) &&
      setVisible(false);

    if (outside) document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [outside, setVisible]);

  const renderHelp = () => {
    if (showModal && visible)
      return createPortal(
        <InfoModal>
          <Text>{children}</Text>
        </InfoModal>,
        document.body
      );
    if (visible && position)
      return createPortal(
        <Info
          ref={tooltip}
          data-testid="help-tooltip"
          visible={visible}
          onClick={e => e.preventDefault()}
          position={position}>
          {children}
        </Info>,
        document.body
      );
    return null;
  };

  return (
    <HelpWrapper ref={outside}>
      <Icon
        type="button"
        title={title}
        onClick={e => {
          e.stopPropagation();
          setVisible(!visible);
        }}
        inverted={inverted}
        color={color}>
        {icon || <FontAwesomeIcon icon={faQuestionCircle} />}
      </Icon>
      {renderHelp()}
    </HelpWrapper>
  );
};

Help.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  icon: PropTypes.any,
  color: PropTypes.string,
  inverted: PropTypes.bool,
  showModal: PropTypes.bool,
  left: PropTypes.bool,
  visible: PropTypes.bool,
  setVisible: PropTypes.func
};

// Style Overrides
const InfoModal = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: ${z("peak")};
  pointer-events: none;

  ${Text} {
    padding: ${pad}px;
    background: ${({theme}) => theme.tertiary};
    border-radius: ${radius};
  }
`;

const HelpWrapper = styled.span`
  position: relative;
  display: inline-block;
  width: min-content;
  z-index: ${z("above")};
`;

const Icon = styled.button`
  svg {
    fill: ${({inverted, theme, color}) => {
      if (color) return color;
      if (inverted) return theme.tertiary;
      return theme.secondary;
    }};
    ${voice.quiet};
  }
`;

const Info = styled.span`
  ${voice.quiet};
  position: fixed;
  z-index: ${z("peak")};
  top: ${({position}) => position?.top || 0}px;
  left: ${({position}) => (position?.left === undefined ? "auto" : `${position.left}px`)};
  right: ${({position}) => (position?.right === undefined ? "auto" : `${position.right}px`)};
  transform: ${({position}) =>
    position?.below
      ? `translateY(${pad / 2}px)`
      : `translateY(calc(-100% + ${pad / 2}px))`};
  padding: ${pad / 2}px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.secondary};
  border-radius: ${radius};
  width: max-content;
  max-width: 300px;
  white-space: break-spaces;
  box-shadow: ${shadow};
  transition: ${transition};
  visibility: ${({visible}) => (visible ? "visible" : "hidden")};
`;

export default Help;
